import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { analyzeArticleWithAI } from '../api';
import { stageDraftText } from '../import/importHandoff';
import usePendingMedia from './usePendingMedia';

const EMPTY = {
  title: '',
  text: '',
  tags: [],
  icon: null,
  summary: '',
  externalUrl: '',
};

/**
 * What an author is writing — the fields, the AI help for them and the media
 * waiting to be uploaded — for the one editor behind creating and editing
 * (ArticleEditorForm). The pages differ only in what the draft starts with and
 * what saving does with it, so that is all they keep.
 *
 * `initial` is read once; `load(values)` starts the draft over, as opening an
 * existing article for editing does.
 */
export default function useArticleDraft(initial = {}) {
  const { t } = useTranslation();

  const [values, setValues] = useState({ ...EMPTY, ...initial });
  const [titleError, setTitleError] = useState('');
  const [error, setError] = useState('');

  const [isAnalyzingWithAI, setIsAnalyzingWithAI] = useState(false);
  const [aiError, setAiError] = useState('');
  const [aiTagSuggestions, setAiTagSuggestions] = useState([]);
  const [isSuggestingTags, setIsSuggestingTags] = useState(false);
  const [aiTagsError, setAiTagsError] = useState('');

  // One analysis serves both the summary and the tags, as long as title and
  // text are what it was asked about.
  const aiAnalysisRef = useRef({ key: '', result: null, promise: null });

  const cursorPositionRef = useRef(0);
  const { stage, uploadPending, discardPending } = usePendingMedia();

  function invalidateAIAnalysis() {
    aiAnalysisRef.current = { key: '', result: null, promise: null };
    setAiTagSuggestions([]);
    setAiError('');
    setAiTagsError('');
  }

  // A change to title or text makes an earlier AI answer stale.
  function set(field, value) {
    setValues((current) => ({
      ...current,
      [field]: typeof value === 'function' ? value(current[field]) : value,
    }));

    if (field === 'title' || field === 'text') {
      invalidateAIAnalysis();
    }

    if (field === 'title') {
      setTitleError('');
    }

    setError('');
  }

  function load(next) {
    invalidateAIAnalysis();
    setValues({ ...EMPTY, ...next });
    setTitleError('');
    setError('');
  }

  async function getAIAnalysis() {
    const title = values.title.trim();
    const key = JSON.stringify([title, values.text]);
    const cache = aiAnalysisRef.current;

    if (cache.key === key && cache.result) return cache.result;
    if (cache.key === key && cache.promise) return cache.promise;

    const promise = analyzeArticleWithAI({ title, text: values.text });
    aiAnalysisRef.current = { key, result: null, promise };

    try {
      const result = await promise;

      if (aiAnalysisRef.current.key === key) {
        aiAnalysisRef.current = { key, result, promise: null };
      }

      return result;
    } catch (err) {
      if (aiAnalysisRef.current.key === key) {
        aiAnalysisRef.current = { key: '', result: null, promise: null };
      }

      throw err;
    }
  }

  // A link item has no text, so a summary needs only the title.
  async function handleAISummary(saving = false) {
    if (isAnalyzingWithAI || saving) return;

    if (!values.title.trim()) {
      setAiError(t('aiAssistant.titleRequired'));
      return;
    }

    try {
      setIsAnalyzingWithAI(true);
      setAiError('');

      const result = await getAIAnalysis();

      if (typeof result?.summary !== 'string') {
        throw new Error('Invalid AI summary response');
      }

      setValues((current) => ({ ...current, summary: result.summary }));
    } catch (err) {
      console.error('AI summary generation failed:', err);
      setAiError(t('aiAssistant.requestFailed'));
    } finally {
      setIsAnalyzingWithAI(false);
    }
  }

  async function handleAISuggestTags(saving = false) {
    if (isSuggestingTags || saving) return;

    if (!values.title.trim() || !values.text.trim()) {
      setAiTagsError(t('aiAssistant.titleRequired'));
      return;
    }

    try {
      setIsSuggestingTags(true);
      setAiTagsError('');
      setAiTagSuggestions([]);

      const result = await getAIAnalysis();

      if (!Array.isArray(result?.tags)) {
        throw new Error('Invalid AI tags response');
      }

      const seen = new Set();

      setAiTagSuggestions(
        result.tags
          .filter((tag) => typeof tag?.name === 'string' && tag.name.trim())
          .map((tag) => ({
            name: tag.name.trim(),
            source: tag.source === 'existing' ? 'existing' : 'new',
          }))
          .filter((tag) => {
            const key = tag.name.toLowerCase();
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
          })
      );
    } catch (err) {
      console.error('AI tag suggestions failed:', err);
      setAiTagsError('Die Tags konnten nicht generiert werden.');
    } finally {
      setIsSuggestingTags(false);
    }
  }

  function acceptAITag(tagName) {
    setValues((current) =>
      current.tags.some((tag) => tag.toLowerCase() === tagName.toLowerCase())
        ? current
        : { ...current, tags: [...current.tags, tagName] }
    );
  }

  function removeTag(tagName) {
    setValues((current) => ({
      ...current,
      tags: current.tags.filter((tag) => tag.toLowerCase() !== tagName.toLowerCase()),
    }));
  }

  // The file is staged, not uploaded: it goes to the server only when the
  // article is saved (usePendingMedia), and the text links it by a blob URL.
  // `insert` puts the link in the visual editor; without it, the link goes
  // into the text at the code view's cursor.
  function insertFile(file, mediaType, insert) {
    if (!file.type.startsWith(`${mediaType}/`)) {
      setError(t('createArticle.invalidFile', { type: mediaType }));
      return false;
    }

    const blobUrl = stage(file, mediaType);
    const cursorPosition = cursorPositionRef.current;
    const markdown = mediaType === 'image'
      ? `![${file.name}](${blobUrl})`
      : `[${file.name}](${blobUrl})`;

    if (insert) {
      insert(markdown);
      return true;
    }

    set('text', (previous) =>
      `${previous.slice(0, cursorPosition)}\n${markdown}\n${previous.slice(cursorPosition)}`
    );
    return true;
  }

  function insertMedia(event, mediaType, insert) {
    const file = event.target.files?.[0];
    event.target.value = '';

    if (file) insertFile(file, mediaType, insert);
  }

  // "Neu importieren": only the text is replaced; title, summary and the
  // sidebar stay as the author set them.
  function replaceTextFromImport(draft) {
    if (draft.warnings?.length) {
      console.warn('Neu importieren:', draft.warnings);
    }

    set('text', stageDraftText(draft, stage));
  }

  return {
    values,
    set,
    load,
    titleError,
    setTitleError,
    error,
    setError,
    cursorPositionRef,
    stage,
    uploadPending,
    discardPending,
    insertMedia,
    insertFile,
    replaceTextFromImport,
    ai: {
      isAnalyzingWithAI,
      aiError,
      aiTagSuggestions,
      isSuggestingTags,
      aiTagsError,
      handleAISummary,
      handleAISuggestTags,
      acceptAITag,
      removeTag,
    },
  };
}
